// Validation feed grouped by category with status filter chips.
import React from "react";
import { AlertTriangle } from "lucide-react";
import type { ValidateFileResult } from "./api/contentRegistryApi";
import {
  STATUS_CONFIG,
  CATEGORY_META,
} from "../../components/ValidationResultPanel";
import {
  PIPELINE_STAGES,
  type ConsolePhase,
  type PipelineRun,
} from "./processingPipeline";
import {
  sortedCategoryKeys,
  categoryTone,
  summaryText,
  countForFilter,
} from "./utils";

type FeedStatusFilter = "ALL" | "PASS" | "FAIL" | "WARN" | "SKIPPED";

const STATUS_FILTER_CHIPS: {
  value: FeedStatusFilter;
  label: string;
  activeCls: string;
}[] = [
  {
    value: "ALL",
    label: "All",
    activeCls:
      "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300",
  },
  {
    value: "PASS",
    label: "Passed",
    activeCls:
      "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300",
  },
  {
    value: "FAIL",
    label: "Failed",
    activeCls:
      "border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300",
  },
  {
    value: "WARN",
    label: "Warnings",
    activeCls:
      "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
  },
  {
    value: "SKIPPED",
    label: "Skipped",
    activeCls:
      "border-slate-300 bg-slate-100 text-slate-700 dark:border-white/20 dark:bg-white/[0.06] dark:text-white/70",
  },
];

export function ExtractionFeed({
  result,
  run,
  phase,
  errorMessage,
}: {
  result: ValidateFileResult | null;
  run: PipelineRun;
  phase: ConsolePhase;
  errorMessage?: string | null;
}) {
  const [statusFilter, setStatusFilter] =
    React.useState<FeedStatusFilter>("ALL");

  if (phase === "error") {
    return (
      <div className="flex flex-1 items-start gap-2.5 px-4 pb-4">
        <AlertTriangle className="mt-0.5 size-4 flex-none text-red-500" />
        <p className="break-words text-xs leading-snug text-muted-foreground">
          {errorMessage ?? "Validation could not be completed."}
        </p>
      </div>
    );
  }

  if (result) {
    const cats = sortedCategoryKeys(result.resultsByCategory);
    if (cats.length === 0) {
      return (
        <div className="flex flex-1 items-start gap-2.5 px-4 pb-4">
          <p className="break-words text-xs leading-snug text-muted-foreground">
            Validation completed but returned no rule results.
          </p>
        </div>
      );
    }
    const overall = result.summary;
    const visibleCats = cats.filter((cat) => {
      const rules = result.resultsByCategory[cat] ?? [];
      return (
        statusFilter === "ALL" ||
        rules.some((r) => r.status === statusFilter)
      );
    });
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 px-2.5 pb-3 lg:overflow-y-auto">
        <div className="sticky top-0 z-[2] -mx-2.5 flex flex-wrap items-center gap-1 border-b border-border bg-background/95 px-2.5 py-1.5 backdrop-blur">
          {STATUS_FILTER_CHIPS.map((chip) => {
            const count = countForFilter(overall, chip.value);
            const active = statusFilter === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => setStatusFilter(chip.value)}
                className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                  active
                    ? chip.activeCls
                    : "border-border bg-transparent text-muted-foreground hover:bg-accent"
                }`}
              >
                {chip.label}
                <span
                  className={`tabular-nums text-[9.5px] font-medium ${
                    active ? "opacity-80" : "opacity-60"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {visibleCats.length === 0 && (
          <div className="flex flex-1 items-start gap-2.5 px-2 pt-2">
            <p className="break-words text-xs leading-snug text-muted-foreground">
              No rules match this filter.
            </p>
          </div>
        )}
        {visibleCats.map((cat) => {
          const allRules = result.resultsByCategory[cat] ?? [];
          const rules =
            statusFilter === "ALL"
              ? allRules
              : allRules.filter((r) => r.status === statusFilter);
          const summary = result.summaryByCategory[cat] ?? {
            pass: 0,
            fail: 0,
            warn: 0,
            skipped: 0,
            total: allRules.length,
          };
          const meta = CATEGORY_META[cat];
          const label = meta?.label ?? cat;
          const tone = categoryTone(summary);
          return (
            <section key={cat} className="flex flex-col gap-1">
              <div
                className={`sticky top-[34px] z-[1] flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1 backdrop-blur-sm ${tone.border} ${tone.bg}`}
              >
                <span
                  className={`size-1.5 flex-none rounded-full ${tone.dot}`}
                />
                <span className="text-[10.5px] font-bold tracking-wide text-foreground">
                  {label}
                </span>
                <span className="ml-auto flex-none whitespace-nowrap text-[9.5px] text-muted-foreground">
                  {summaryText(summary)} · {summary.total} total
                </span>
              </div>
              {rules.map((r, i) => {
                const s =
                  STATUS_CONFIG[r.status as keyof typeof STATUS_CONFIG] ??
                  STATUS_CONFIG.SKIPPED;
                const Icon = s.icon;
                const detail =
                  r.status === "SKIPPED" ? r.skipReason : r.remediation;
                return (
                  <div
                    key={`${r.ruleId}-${i}`}
                    className={`rounded-md border border-border px-2 py-1.5 ${s.rowBg || "bg-[#f8fafc] dark:bg-white/[0.02]"}`}
                    style={{
                      animation: "rgc-slidein .4s cubic-bezier(.2,.8,.2,1)",
                    }}
                  >
                    <div className="mb-0.5 flex items-center gap-1.5">
                      <Icon className={`size-3 flex-none ${s.iconCls}`} />
                      <span
                        className={`flex-none whitespace-nowrap rounded border px-1 py-0.5 text-[9.5px] font-bold ${s.pillCls}`}
                      >
                        {s.label}
                      </span>
                      {r.ruleId && (
                        <span className="ml-auto flex-none font-mono text-[9.5px] font-medium text-muted-foreground">
                          {r.ruleId}
                        </span>
                      )}
                    </div>
                    <div
                      className="break-words text-[11.5px] leading-snug text-foreground"
                      title={r.name}
                    >
                      {r.name}
                    </div>
                    {detail && (
                      <div
                        className="mt-0.5 line-clamp-2 break-words text-[10px] leading-snug text-muted-foreground"
                        title={detail}
                      >
                        {detail}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5 px-3 pb-3 lg:overflow-y-auto">
      {PIPELINE_STAGES.slice(0, run.stage + 1)
        .reverse()
        .map((s, idx) => (
          <div
            key={s.key}
            className={`rounded-lg border px-2.5 py-1.5 ${
              idx === 0
                ? "border-indigo-200 bg-indigo-50/60 dark:border-indigo-500/30 dark:bg-indigo-500/[0.06]"
                : "border-border bg-[#f8fafc] dark:bg-white/[0.02]"
            }`}
            style={{ animation: "rgc-slidein .4s cubic-bezier(.2,.8,.2,1)" }}
          >
            <div
              className={`text-[11px] font-semibold ${
                idx === 0
                  ? "text-indigo-600 dark:text-indigo-400"
                  : "text-muted-foreground"
              }`}
            >
              {s.label}
            </div>
            <div className="mt-0.5 break-words text-[10px] leading-snug text-muted-foreground">
              {s.hint}
            </div>
          </div>
        ))}
    </div>
  );
}
